import { isIOSWebKit } from './browser.js';

/** CSS var synced from visualViewport while a mobile sheet is open (see syncViewportHeightNow). */
const VIEWPORT_HEIGHT_FULL_CSS = "var(--viewport-height-full, 100dvh)";
const VIEWPORT_OFFSET_TOP_CSS = "var(--viewport-offset-top, 0px)";
/** Gap the keyboard leaves at the bottom of the layout viewport (see measureKeyboardInset). */
const VIEWPORT_KEYBOARD_INSET_CSS = "var(--viewport-keyboard-inset, 0px)";
const SWIPEABLE_SHEET_OPEN_SELECTOR = ".ReactSwipeableBottomSheet--open";
const VIEWPORT_HEIGHT_CSS_VARS = ["--viewport-height-full", "--viewport-height-p95", "--viewport-height-p90"];
const VIEWPORT_OFFSET_TOP_VAR = "--viewport-offset-top";
const VIEWPORT_KEYBOARD_INSET_VAR = "--viewport-keyboard-inset";
/**
 * Shrink beyond which a viewport change is treated as the soft keyboard rather than
 * browser chrome or zoom, and the sheet is allowed to follow it down.
 *
 * ponytail: naive threshold — keyboards are ~250-350px, URL bars ~50-90px, so anything in
 * between is guesswork. Upgrade to `navigator.virtualKeyboard` (VirtualKeyboard API) once
 * Safari ships it and the geometry can be read directly instead of inferred.
 */
const KEYBOARD_MIN_INSET_PX = 120;
let sheetViewportSession = null;
let lastAppliedViewportHeight = 0;
let lastAppliedViewportOffsetTop = 0;
let lastAppliedKeyboardInset = -1;
let wasInputFocusZoomActive = false;
const getVisualViewportScale = () => {
    const vv = window.visualViewport;
    if (!vv) {
        return 1;
    }
    if (typeof vv.scale === "number" && vv.scale > 0) {
        return vv.scale;
    }
    return 1;
};
/** iOS Safari input-focus zoom — not URL-bar shrink (scale stays 1 there). */
const isPageInputFocusZoomActive = () => {
    if (typeof window === "undefined") {
        return false;
    }
    return getVisualViewportScale() > 1.01;
};
const isInputFocusZoomActive = isPageInputFocusZoomActive;
const measureViewportCandidate = () => {
    var _a;
    if (typeof window === "undefined") {
        return 0;
    }
    const vv = window.visualViewport;
    // Input-focus zoom shrinks visualViewport.height; innerHeight stays at layout size.
    // Locking the zoomed vv height crops the sheet after zoom resets (homepage → CTA).
    if (vv && isInputFocusZoomActive()) {
        return Math.round(window.innerHeight);
    }
    // Visible layout height — avoids sheet extending under the mobile browser top bar.
    return Math.round((_a = vv === null || vv === void 0 ? void 0 : vv.height) !== null && _a !== void 0 ? _a : window.innerHeight);
};
const measureViewportOffsetTop = () => {
    var _a, _b;
    if (typeof window === "undefined") {
        return 0;
    }
    if (isInputFocusZoomActive()) {
        return 0;
    }
    // Body scroll-lock uses position:fixed — offsetTop reflects page scroll, not sheet position.
    if (document.body.style.position === "fixed") {
        return 0;
    }
    return Math.round((_b = (_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.offsetTop) !== null && _b !== void 0 ? _b : 0);
};
/** Read the visible viewport height (visualViewport on iOS, innerHeight fallback). */
const getVisualViewportHeight = () => {
    return measureViewportCandidate();
};
const resolveSheetSyncHeight = () => {
    const candidate = measureViewportCandidate();
    const zoomActive = isInputFocusZoomActive();
    if (!sheetViewportSession) {
        wasInputFocusZoomActive = zoomActive;
        return candidate;
    }
    // Re-lock when iOS input-focus zoom resets so a zoomed-small height cannot persist.
    if (wasInputFocusZoomActive && !zoomActive) {
        sheetViewportSession.lockedHeight = candidate;
    }
    else if (candidate > sheetViewportSession.lockedHeight) {
        sheetViewportSession.lockedHeight = candidate;
    }
    else if (sheetViewportSession.lockedHeight - candidate >= KEYBOARD_MIN_INSET_PX) {
        // A drop this large is the soft keyboard. The sheet has to follow it down, otherwise
        // the sticky footer stays pinned to the bottom of a full-height panel, under the keyboard.
        sheetViewportSession.lockedHeight = candidate;
    }
    wasInputFocusZoomActive = zoomActive;
    return sheetViewportSession.lockedHeight;
};
const beginSheetViewportSession = () => {
    sheetViewportSession = { lockedHeight: measureViewportCandidate() };
};
const endSheetViewportSession = () => {
    sheetViewportSession = null;
    lastAppliedViewportHeight = 0;
    lastAppliedViewportOffsetTop = 0;
    lastAppliedKeyboardInset = -1;
    wasInputFocusZoomActive = false;
};
const setViewportHeightCssVars = (height) => {
    const root = document.documentElement;
    root.style.setProperty("--viewport-height-full", `${height}px`);
    root.style.setProperty("--viewport-height-p95", `${Math.round(height * 0.95)}px`);
    root.style.setProperty("--viewport-height-p90", `${Math.round(height * 0.9)}px`);
};
const clearViewportHeightCssVars = () => {
    const root = document.documentElement;
    VIEWPORT_HEIGHT_CSS_VARS.forEach((name) => {
        root.style.removeProperty(name);
    });
    root.style.removeProperty(VIEWPORT_OFFSET_TOP_VAR);
    root.style.removeProperty(VIEWPORT_KEYBOARD_INSET_VAR);
};
const syncSwipeableSheetDomHeight = (height) => {
    document.querySelectorAll(SWIPEABLE_SHEET_OPEN_SELECTOR).forEach((node) => {
        if (!(node instanceof HTMLElement)) {
            return;
        }
        node.style.height = `${height}px`;
        node.style.maxHeight = `${height}px`;
    });
};
const clearSwipeableSheetDomHeight = () => {
    document.querySelectorAll(SWIPEABLE_SHEET_OPEN_SELECTOR).forEach((node) => {
        if (!(node instanceof HTMLElement)) {
            return;
        }
        node.style.removeProperty("height");
        node.style.removeProperty("maxHeight");
    });
};
/**
 * Bottom gap the keyboard occupies in the layout viewport.
 *
 * Browsers honouring `interactive-widget=resizes-content` shrink `innerHeight` with the
 * keyboard and report 0 here. iOS Safari ignores that token and only shrinks the visual
 * viewport, so a bottom-anchored sheet needs the gap spelled out to stay above the keyboard.
 */
const measureKeyboardInset = (height, offsetTop) => Math.max(0, Math.round(window.innerHeight - height - offsetTop));
const applySheetViewportGeometry = () => {
    const height = resolveSheetSyncHeight();
    const offsetTop = measureViewportOffsetTop();
    const keyboardInset = measureKeyboardInset(height, offsetTop);
    const heightChanged = height !== lastAppliedViewportHeight;
    const offsetChanged = offsetTop !== lastAppliedViewportOffsetTop;
    const keyboardInsetChanged = keyboardInset !== lastAppliedKeyboardInset;
    if (!heightChanged && !offsetChanged && !keyboardInsetChanged) {
        return;
    }
    if (keyboardInsetChanged) {
        lastAppliedKeyboardInset = keyboardInset;
        document.documentElement.style.setProperty(VIEWPORT_KEYBOARD_INSET_VAR, `${keyboardInset}px`);
    }
    if (heightChanged) {
        lastAppliedViewportHeight = height;
        setViewportHeightCssVars(height);
        syncSwipeableSheetDomHeight(height);
        window.dispatchEvent(new Event("resize"));
    }
    if (offsetChanged) {
        lastAppliedViewportOffsetTop = offsetTop;
        document.documentElement.style.setProperty(VIEWPORT_OFFSET_TOP_VAR, `${offsetTop}px`);
    }
};
/**
 * Push viewport height into CSS vars, react-swipeable-bottom-sheet inline styles,
 * and a synthetic window resize (library HeightUpdater only listens to resize).
 */
const syncViewportHeightNow = () => {
    if (typeof document === "undefined" || typeof window === "undefined") {
        return 0;
    }
    const height = resolveSheetSyncHeight();
    applySheetViewportGeometry();
    return height;
};
const delayMs = (ms) => new Promise((resolve) => {
    window.setTimeout(resolve, ms);
});
/**
 * On iOS Safari, wait for input-focus zoom to reset after blurring fields.
 * Use before opening a full-viewport bottom sheet from a page-level form.
 */
const waitForInputFocusZoomReset = (options = {}) => {
    if (typeof window === "undefined" || !isIOSWebKit()) {
        return Promise.resolve();
    }
    const { timeoutMs = 900, minWaitMs = 320, pollIntervalMs = 40, stableSamplesRequired = 2 } = options;
    const startedAt = Date.now();
    const finishAfterMinWait = () => {
        const remaining = Math.max(0, minWaitMs - (Date.now() - startedAt));
        return remaining > 0 ? delayMs(remaining) : Promise.resolve();
    };
    if (!isInputFocusZoomActive()) {
        return finishAfterMinWait();
    }
    return new Promise((resolve) => {
        let settled = false;
        let stableSamples = 0;
        let pollId = 0;
        let timeoutId = 0;
        const vv = window.visualViewport;
        const cleanup = () => {
            window.clearInterval(pollId);
            window.clearTimeout(timeoutId);
            vv === null || vv === void 0 ? void 0 : vv.removeEventListener("resize", onViewportChange);
            vv === null || vv === void 0 ? void 0 : vv.removeEventListener("scroll", onViewportChange);
        };
        const finish = () => {
            if (settled) {
                return;
            }
            settled = true;
            cleanup();
            void finishAfterMinWait().then(resolve);
        };
        const sampleZoomState = () => {
            if (!isInputFocusZoomActive()) {
                stableSamples += 1;
                if (stableSamples >= stableSamplesRequired) {
                    finish();
                }
                return;
            }
            stableSamples = 0;
        };
        const onViewportChange = () => {
            sampleZoomState();
        };
        vv === null || vv === void 0 ? void 0 : vv.addEventListener("resize", onViewportChange);
        vv === null || vv === void 0 ? void 0 : vv.addEventListener("scroll", onViewportChange);
        pollId = window.setInterval(sampleZoomState, pollIntervalMs);
        timeoutId = window.setTimeout(finish, timeoutMs);
        sampleZoomState();
    });
};
const SHEET_OPEN_SYNC_BURST_MS = [50, 150, 300, 500, 700];
/**
 * iOS Safari input-focus zoom changes visualViewport; react-swipeable-bottom-sheet
 * caches window.innerHeight and only listens to window resize — not visualViewport.
 *
 * Sheet height uses a monotonic lock so OTP/keyboard focus does not shrink the sheet.
 */
const syncVisualViewportHeightVar = (options) => {
    if (typeof window === "undefined") {
        return () => { };
    }
    const vv = window.visualViewport;
    let syncBurstTimeoutIds = [];
    let deferredStartFrame = 0;
    let syncFrame = 0;
    let sessionStarted = false;
    let disposed = false;
    const clearSyncBurst = () => {
        syncBurstTimeoutIds.forEach((id) => window.clearTimeout(id));
        syncBurstTimeoutIds = [];
    };
    const sync = () => {
        syncViewportHeightNow();
    };
    const remeasureSessionLock = () => {
        if (!sheetViewportSession || isInputFocusZoomActive()) {
            return;
        }
        const fresh = measureViewportCandidate();
        if (fresh > sheetViewportSession.lockedHeight) {
            sheetViewportSession.lockedHeight = fresh;
        }
    };
    const startSession = () => {
        if (sessionStarted || disposed) {
            return;
        }
        sessionStarted = true;
        beginSheetViewportSession();
        remeasureSessionLock();
        sync();
        requestAnimationFrame(() => {
            remeasureSessionLock();
            sync();
        });
        syncBurstTimeoutIds = SHEET_OPEN_SYNC_BURST_MS.map((delayMs) => window.setTimeout(() => {
            remeasureSessionLock();
            sync();
        }, delayMs));
    };
    const waitForZoomResetThenStart = (frameBudget = 60) => {
        let frames = 0;
        const tick = () => {
            if (disposed || sessionStarted) {
                return;
            }
            if (!isInputFocusZoomActive() || frames >= frameBudget) {
                startSession();
                return;
            }
            frames += 1;
            deferredStartFrame = requestAnimationFrame(tick);
        };
        deferredStartFrame = requestAnimationFrame(tick);
    };
    if (options === null || options === void 0 ? void 0 : options.deferSessionStart) {
        // Apply full layout height immediately while zoom resets (see measureViewportCandidate).
        sync();
        waitForZoomResetThenStart();
    }
    else {
        startSession();
    }
    if (!vv) {
        return () => {
            disposed = true;
            cancelAnimationFrame(deferredStartFrame);
            clearSyncBurst();
            endSheetViewportSession();
        };
    }
    const scheduleSync = () => {
        cancelAnimationFrame(syncFrame);
        syncFrame = requestAnimationFrame(sync);
    };
    vv.addEventListener("resize", scheduleSync);
    vv.addEventListener("scroll", scheduleSync);
    return () => {
        disposed = true;
        cancelAnimationFrame(deferredStartFrame);
        cancelAnimationFrame(syncFrame);
        clearSyncBurst();
        vv.removeEventListener("resize", scheduleSync);
        vv.removeEventListener("scroll", scheduleSync);
        endSheetViewportSession();
        clearSwipeableSheetDomHeight();
        clearViewportHeightCssVars();
    };
};
const usesViewportSheetLayout = (disableSheetZoom, fullScreen) => Boolean(disableSheetZoom && !fullScreen);
const getViewportSheetContainerStyle = () => ({
    height: VIEWPORT_HEIGHT_FULL_CSS,
    maxHeight: VIEWPORT_HEIGHT_FULL_CSS,
    overflow: "hidden"
});

export { VIEWPORT_HEIGHT_FULL_CSS, VIEWPORT_KEYBOARD_INSET_CSS, VIEWPORT_OFFSET_TOP_CSS, getViewportSheetContainerStyle, getVisualViewportHeight, isPageInputFocusZoomActive, syncViewportHeightNow, syncVisualViewportHeightVar, usesViewportSheetLayout, waitForInputFocusZoomReset };
//# sourceMappingURL=viewportSheet.js.map
